import * as React from 'react';
import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import { useWebinarT } from '@/hooks/useWebinarT';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

const PRIORITY_COUNTRIES: RPNInput.Country[] = ['FR', 'BE', 'CA', 'AE', 'CH'];

type PhoneInputProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value' | 'ref'> &
  Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
    onChange?: (value: RPNInput.Value) => void;
    label?: string;
    classNameLabel?: string;
    theme?: 'LIGHT' | 'DARK';
  };

const findCountryByCallingCode = (callingCode: string): RPNInput.Country | undefined => {
  const countries = RPNInput.getCountries();
  for (const country of countries) {
    if (RPNInput.getCountryCallingCode(country) === callingCode) {
      return country;
    }
  }
  return undefined;
};

const PhoneSelectInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(({ className, onChange, value, label, defaultCountry, classNameLabel, theme, ...props }, ref) => {
  const [country, setCountry] = React.useState<RPNInput.Country | undefined>(
    defaultCountry || undefined,
  );

  const containerRef = React.useRef<HTMLDivElement | null>(null);

  // Autofill navigateur : Chrome/Safari ne déclenchent pas le onChange React du
  // champ téléphone (react-phone-number-input avale la valeur autofillée), donc
  // le numéro affiché n'est jamais poussé dans le formulaire. L'autofill déclenche
  // l'animation CSS "onAutoFillStart" : on relit la valeur DOM et on redispatche un
  // event input pour que la lib la prenne en compte normalement.
  React.useEffect(() => {
    const inputElement = containerRef.current?.querySelector('input');
    if (!inputElement) {
      return;
    }
    const handleAutoFill = (event: AnimationEvent) => {
      if (event.animationName !== 'onAutoFillStart') {
        return;
      }
      const nativeValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      )?.set;
      nativeValueSetter?.call(inputElement, inputElement.value);
      inputElement.dispatchEvent(new Event('input', { bubbles: true }));
    };
    inputElement.addEventListener('animationstart', handleAutoFill);
    return () => inputElement.removeEventListener('animationstart', handleAutoFill);
  }, []);

  // Extract country from value if it's an international number
  React.useEffect(() => {
    if (value && typeof value === 'string' && value.startsWith('+')) {
      try {
        const phoneNumber = RPNInput.parsePhoneNumber(value as string);
        if (phoneNumber?.country) {
          setCountry(phoneNumber.country as RPNInput.Country);
          return;
        }
      } catch {
        // If parsing fails, try to detect country from calling code
        // This handles cases where user is typing the calling code
      }

      // Try to detect country from calling code even if parsing failed
      const callingCodeMatch = value.match(/^\+(\d{1,4})/);
      if (callingCodeMatch) {
        const callingCode = callingCodeMatch[1];
        const foundCountry = findCountryByCallingCode(callingCode);
        if (foundCountry) {
          setCountry(foundCountry);
          return;
        }
      }
    } else if (defaultCountry) {
      // If no value or value doesn't start with +, use default country
      setCountry(defaultCountry);
    }
  }, [value, defaultCountry]);

  const handleChange = (newValue: RPNInput.Value | undefined) => {
    if (newValue && typeof newValue === 'string' && newValue.length > 20) {
      newValue = newValue.slice(0, 20) as RPNInput.Value;
    }
    onChange?.(newValue || ('' as RPNInput.Value));
  };

  const handleCountryChange = (newCountry: RPNInput.Country) => {
    setCountry(newCountry);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative px-2 py-1 h-14 rounded-lg border border-input bg-white flex items-center',
        theme === 'LIGHT' ? 'bg-gray-100/30' : theme === 'DARK' ? 'bg-white/10' : 'bg-white',
        className,
      )}
    >
      {label && (
        <label
          className={cn(
            'absolute top-0 left-3 px-1 text-sm pointer-events-none',
            classNameLabel || 'text-tertiary',
          )}
        >
          {label}
        </label>
      )}
      <div className={cn('flex-1 flex items-center', label ? 'pt-4' : '')}>
        <RPNInput.default
          ref={ref}
          className={cn('flex flex-1', theme === 'LIGHT' ? 'text-black' : 'text-white')}
          flagComponent={FlagComponent}
          countrySelectComponent={(selectProps) => (
            <CountrySelect {...selectProps} onCountryChange={handleCountryChange} />
          )}
          inputComponent={InputComponent}
          international
          withCountryCallingCode
          defaultCountry={defaultCountry}
          country={country}
          smartCaret={false}
          value={value || undefined}
          onChange={handleChange}
          {...props}
        />
      </div>
    </div>
  );
});
PhoneSelectInput.displayName = 'PhoneSelectInput';

const InputComponent = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, maxLength = 20, ...props }, ref) => (
    <Input
      className={cn(
        'rounded-e-lg px-0 rounded-s-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none text-lg md:text-base',
        className,
      )}
      maxLength={maxLength}
      {...props}
      ref={ref}
    />
  ),
);
InputComponent.displayName = 'InputComponent';

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
  onCountryChange?: (country: RPNInput.Country) => void;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
  onCountryChange,
}: CountrySelectProps) => {
  const { t } = useWebinarT('user');
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);

  const handleChange = (country: RPNInput.Country) => {
    onChange(country);
    onCountryChange?.(country);
  };

  const priorityCountries = countryList.filter(
    (entry) => entry.value && PRIORITY_COUNTRIES.includes(entry.value),
  );
  const otherCountries = countryList.filter(
    (entry) => !entry.value || !PRIORITY_COUNTRIES.includes(entry.value),
  );

  const sortedPriorityCountries = priorityCountries.sort((a, b) => {
    if (!a.value || !b.value) return 0;
    const indexA = PRIORITY_COUNTRIES.indexOf(a.value);
    const indexB = PRIORITY_COUNTRIES.indexOf(b.value);
    return indexA - indexB;
  });

  const showSeparator = priorityCountries.length > 0 && otherCountries.length > 0;

  return (
    <div>
      <Popover
        open={isOpen}
        modal
        onOpenChange={(open) => {
          setIsOpen(open);
          open && setSearchValue('');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="flex gap-1 rounded-e-none rounded-s-lg border-r-0 border-l-0 border-t-0 border-b-0 px-3 focus:z-10 shadow-none"
            disabled={disabled}
          >
            <FlagComponent country={selectedCountry} countryName={selectedCountry} />
            <ChevronsUpDown
              className={cn('-mr-2 size-4 opacity-50', disabled ? 'hidden' : 'opacity-100')}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0">
          <Command>
            <CommandInput
              value={searchValue}
              onValueChange={(value) => {
                setSearchValue(value);
                setTimeout(() => {
                  if (scrollAreaRef.current) {
                    const viewportElement = scrollAreaRef.current.querySelector(
                      '[data-radix-scroll-area-viewport]',
                    );
                    if (viewportElement) {
                      viewportElement.scrollTop = 0;
                    }
                  }
                }, 0);
              }}
              placeholder={t('personalData.phoneNumber.searchPlaceholder')}
            />
            <CommandList>
              <ScrollArea ref={scrollAreaRef} className="h-72">
                <CommandEmpty>{t('personalData.phoneNumber.noCountryFound')}</CommandEmpty>
                {sortedPriorityCountries.length > 0 && (
                  <CommandGroup>
                    {sortedPriorityCountries.map(({ value, label }) =>
                      value ? (
                        <CountrySelectOption
                          key={value}
                          country={value}
                          countryName={label}
                          selectedCountry={selectedCountry}
                          onChange={handleChange}
                          onSelectComplete={() => setIsOpen(false)}
                        />
                      ) : null,
                    )}
                  </CommandGroup>
                )}
                {showSeparator && <div className="border-t border-input my-1" role="separator" />}
                {otherCountries.length > 0 && (
                  <CommandGroup>
                    {otherCountries.map(({ value, label }) =>
                      value ? (
                        <CountrySelectOption
                          key={value}
                          country={value}
                          countryName={label}
                          selectedCountry={selectedCountry}
                          onChange={handleChange}
                          onSelectComplete={() => setIsOpen(false)}
                        />
                      ) : null,
                    )}
                  </CommandGroup>
                )}
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country;
  onChange: (country: RPNInput.Country) => void;
  onSelectComplete: () => void;
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: CountrySelectOptionProps) => {
  const handleSelect = () => {
    onChange(country);
    onSelectComplete();
  };

  return (
    <CommandItem
      className="gap-2 data-[selected=true]:bg-[#FBFBFB] data-[selected=true]:text-accent-foreground"
      onSelect={handleSelect}
    >
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-foreground/50">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? 'opacity-100' : 'opacity-0'}`}
      />
    </CommandItem>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

export { PhoneSelectInput };
